import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { diiaInitialState } from './initialState.ts';
import {
  diiaSignGetFinalStatus,
  diiaSignGetStatus,
  diiaSignInitialize,
  diiaSignRetrieveDocs,
} from './thunks/signatureThunks.ts';
import {
  diiaPending,
  diiaError,
  initializeFulfilled,
  initializePending,
  setSignStatus,
  diiaFinalStatusError,
} from './actions/actions.ts';
import {
  getFinalStatusDiiaVerificationProcessingPersonal,
  getStatusDiiaVerificationProcessingPersonal,
  initializeDiiaVerificationProcessingPersonal,
  revokeGetFinalStatusDiiaVerificationProcessingPersonal,
  revokeGetStatusDiiaVerificationProcessingPersonal,
  revokeInitializeDiiaVerificationProcessingPersonal,
} from '~/store/diia/thunks/verificationProcessingPersonalThunks.ts';
import {
  diiaSignGetStatusAssetManager,
  diiaSignInitializeAssetManager,
  diiaSignRetrieveDocsAssetManager,
} from '~/store/diia/thunks/signatureAssetManagerThunks.ts';
import {
  getStatusDiiaSignatureProcessingPersonal,
  initializeDiiaSignatureProcessingPersonal,
  revokeGetStatusDiiaSignatureProcessingPersonal,
  revokeInitializeDiiaSignatureProcessingPersonal,
} from '~/store/diia/thunks/signatureProcessingPersonalThunks.ts';
import {
  deleteDiiaVerification,
  diiaVerificationGetFinalStatus,
  diiaVerificationGetFinalStatusAssetManager,
  diiaVerificationGetStatus,
  diiaVerificationGetStatusAssetManager,
  diiaVerificationInitialize,
  diiaVerificationInitializeAssetManager,
  updateFromDiiaVerification,
  diiaVerificationRetrieveDocuments,
} from './thunks/identificationVerificationThunks.ts';
import {
  covenantsDiiaSignGetFinalStatus,
  covenantsDiiaSignGetStatus,
  covenantsDiiaSignInitialize,
} from '~/store/diia/thunks/signatureCovenantsThunks.ts';

const diiaSlice = createSlice({
  name: 'diia',
  initialState: diiaInitialState,
  reducers: {
    clearDiiaState() {
      return diiaInitialState;
    },
    setDiiaSignError(state, action: PayloadAction<string>) {
      state.error = action.payload;
      state.loading = false;
    },
    setDiiaSignStatus(state, action: PayloadAction<{ status: string }>) {
      setSignStatus(state, action);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(diiaSignInitializeAssetManager.fulfilled, initializeFulfilled)
      .addCase(diiaSignInitializeAssetManager.pending, initializePending)
      .addCase(diiaSignInitializeAssetManager.rejected, diiaError)

      .addCase(diiaSignInitialize.fulfilled, initializeFulfilled)
      .addCase(diiaSignInitialize.pending, initializePending)
      .addCase(diiaSignInitialize.rejected, diiaError)

      .addCase(diiaSignGetStatusAssetManager.fulfilled, setSignStatus)
      .addCase(diiaSignGetStatusAssetManager.rejected, diiaError)

      .addCase(diiaSignGetStatus.fulfilled, setSignStatus)
      .addCase(diiaSignGetStatus.rejected, diiaError)

      .addCase(diiaSignGetFinalStatus.pending, diiaPending)
      .addCase(diiaSignGetFinalStatus.fulfilled, setSignStatus)
      .addCase(diiaSignGetFinalStatus.rejected, diiaError)

      .addCase(diiaSignRetrieveDocsAssetManager.rejected, diiaError)
      .addCase(diiaSignRetrieveDocs.rejected, diiaError)

      .addCase(initializeDiiaVerificationProcessingPersonal.fulfilled, initializeFulfilled)
      .addCase(initializeDiiaVerificationProcessingPersonal.pending, initializePending)
      .addCase(initializeDiiaVerificationProcessingPersonal.rejected, diiaError)
      .addCase(getStatusDiiaVerificationProcessingPersonal.fulfilled, setSignStatus)
      .addCase(getStatusDiiaVerificationProcessingPersonal.rejected, diiaError)

      .addCase(revokeInitializeDiiaVerificationProcessingPersonal.fulfilled, initializeFulfilled)
      .addCase(revokeInitializeDiiaVerificationProcessingPersonal.pending, initializePending)
      .addCase(revokeInitializeDiiaVerificationProcessingPersonal.rejected, diiaError)
      .addCase(revokeGetStatusDiiaVerificationProcessingPersonal.fulfilled, setSignStatus)
      .addCase(revokeGetStatusDiiaVerificationProcessingPersonal.rejected, diiaError)

      .addCase(initializeDiiaSignatureProcessingPersonal.fulfilled, initializeFulfilled)
      .addCase(initializeDiiaSignatureProcessingPersonal.pending, initializePending)
      .addCase(initializeDiiaSignatureProcessingPersonal.rejected, diiaError)
      .addCase(getStatusDiiaSignatureProcessingPersonal.fulfilled, setSignStatus)
      .addCase(getStatusDiiaSignatureProcessingPersonal.rejected, diiaError)

      .addCase(getFinalStatusDiiaVerificationProcessingPersonal.fulfilled, setSignStatus)
      .addCase(getFinalStatusDiiaVerificationProcessingPersonal.rejected, diiaFinalStatusError)
      .addCase(getFinalStatusDiiaVerificationProcessingPersonal.pending, diiaPending)

      .addCase(diiaVerificationInitialize.pending, initializePending)
      .addCase(diiaVerificationInitialize.fulfilled, initializeFulfilled)
      .addCase(diiaVerificationInitialize.rejected, diiaError)
      .addCase(diiaVerificationGetStatus.fulfilled, setSignStatus)
      .addCase(diiaVerificationGetStatus.rejected, diiaError)
      .addCase(diiaVerificationRetrieveDocuments.rejected, diiaError)

      .addCase(diiaVerificationGetFinalStatus.fulfilled, setSignStatus)
      .addCase(diiaVerificationGetFinalStatus.rejected, diiaError)
      .addCase(diiaVerificationGetFinalStatus.pending, diiaPending)

      .addCase(diiaVerificationInitializeAssetManager.pending, initializePending)
      .addCase(diiaVerificationInitializeAssetManager.fulfilled, initializeFulfilled)
      .addCase(diiaVerificationInitializeAssetManager.rejected, diiaError)

      .addCase(diiaVerificationGetStatusAssetManager.fulfilled, setSignStatus)
      .addCase(diiaVerificationGetStatusAssetManager.rejected, diiaError)

      .addCase(diiaVerificationGetFinalStatusAssetManager.fulfilled, setSignStatus)
      .addCase(diiaVerificationGetFinalStatusAssetManager.rejected, diiaError)
      .addCase(diiaVerificationGetFinalStatusAssetManager.pending, diiaPending)

      .addCase(revokeInitializeDiiaSignatureProcessingPersonal.fulfilled, initializeFulfilled)
      .addCase(revokeInitializeDiiaSignatureProcessingPersonal.pending, initializePending)
      .addCase(revokeInitializeDiiaSignatureProcessingPersonal.rejected, diiaError)
      .addCase(revokeGetStatusDiiaSignatureProcessingPersonal.fulfilled, setSignStatus)
      .addCase(revokeGetStatusDiiaSignatureProcessingPersonal.rejected, diiaError)
      .addCase(
        revokeGetFinalStatusDiiaVerificationProcessingPersonal.rejected,
        diiaFinalStatusError
      )
      .addCase(revokeGetFinalStatusDiiaVerificationProcessingPersonal.fulfilled, setSignStatus)
      .addCase(revokeGetFinalStatusDiiaVerificationProcessingPersonal.pending, diiaPending)

      .addCase(covenantsDiiaSignInitialize.fulfilled, initializeFulfilled)
      .addCase(covenantsDiiaSignInitialize.pending, initializePending)
      .addCase(covenantsDiiaSignInitialize.rejected, diiaError)
      .addCase(covenantsDiiaSignGetStatus.fulfilled, setSignStatus)
      .addCase(covenantsDiiaSignGetStatus.rejected, diiaError)
      .addCase(covenantsDiiaSignGetFinalStatus.pending, diiaPending)
      .addCase(covenantsDiiaSignGetFinalStatus.fulfilled, setSignStatus)
      .addCase(covenantsDiiaSignGetFinalStatus.rejected, diiaError)

      .addCase(updateFromDiiaVerification.pending, diiaPending)
      .addCase(updateFromDiiaVerification.fulfilled, setSignStatus)
      .addCase(updateFromDiiaVerification.rejected, diiaError)

      .addCase(deleteDiiaVerification.pending, diiaPending)
      .addCase(deleteDiiaVerification.fulfilled, setSignStatus)
      .addCase(deleteDiiaVerification.rejected, diiaError);
  },
});

export const { clearDiiaState, setDiiaSignError, setDiiaSignStatus } = diiaSlice.actions;

export default diiaSlice.reducer;
